import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ViewContext } from 'components/lib';
import { UMLoader } from '@urbanmetrix/um-kit';
import AddProject from 'views/add-project/AddProject';
import AddGrid from 'views/add-grid/AddGrid';
import AddGeodata from 'views/add-geodata/AddGeodata';
import AddSimulation from 'views/add-simulation/AddSimulation';
import { CurrentProjectContext } from 'contexts/CurrentProjectContext';
import { GridContext } from 'contexts/GridContext';
import { GeodataContext } from 'contexts/GeodataContext';
import GridContextProvider from 'contexts/GridContext';
import GeodataContextProvider from 'contexts/GeodataContext';
import SimulationContextProvider from 'contexts/SimulationContext';
import { getProgress } from 'api/progressApi';
import { getProjectWithChildren } from 'api/projectApi';
import { getGeodata } from 'api/geodataApi';
import { getGrid, getGridVolumeUsage, replaceGrid, type GridVolumeUsage } from 'api/gridApi';
import { getApiErrorMessage } from 'api/apiClient';
import { buildGridParamsPayload, type RawGridParams } from 'views/add-grid/gridPayload';
import { VISUALIZER_PREVIEW_RELOAD_EVENT } from 'views/add-grid/map/MapPreviewModeFrame';
import { enrichGridParamsPayloadWithHeight } from 'services/gridHeightEnrichment';
import { buildGridGeodataPayload, buildGridGeodataReplacePayload } from 'views/add-geodata/persistDraftGeodata';
import { normalizeProjectCoordinatesForMap } from 'utils/coordinateSystemManager';
import ProjectCreationPersistentPreview, {
  type ProjectCreationPreviewState,
} from 'views/project-creation/ProjectCreationPersistentPreview';
import type { GridInsert } from '@urbanmetrix/sdk';
import type { ProjectNode } from 'types/project';
import VolumeUsageDialog from 'components/volume-usage-dialog/VolumeUsageDialog';
import { getGeodataModuleLimitMessage, getGeodataRequiredModuleCount } from 'services/geodataUsage';

type FlowStep = 'project' | 'grid' | 'geodata' | 'simulation';
const GEODATA_BUSY_STATUSES = new Set(['INITIALIZING', 'RUNNING']);
const GEODATA_POLLING_STOP_STATUSES = new Set(['SUCCEEDED']);
const GEODATA_FAILURE_STATUSES = new Set(['FAILED']);
const GEODATA_FAILURE_MESSAGE =
  'Preview update failed. You can keep editing and try Update Preview again when the issue is resolved.';
const getGeodataPollingDelayMs = (elapsedMs: number) => {
  if (elapsedMs < 5_000) {
    return 1_000;
  }
  if (elapsedMs < 15_000) {
    return 2_000;
  }
  if (elapsedMs < 25_000) {
    return 3_000;
  }
  if (elapsedMs < 45_000) {
    return 5_000;
  }
  return 10_000;
};
const PREVIEW_TRACKED_GRID_FILE_KEYS = ['fileForUploadedScene', 'fileForTrees'] as const;
const PREVIEW_TRACKED_GEODATA_FILE_KEYS = ['fileForCustomObstructions', 'fileForProjectSketches'] as const;

type ComparablePreviewGeodata = Parameters<typeof buildGridGeodataPayload>[0];
type ComparablePreviewGeodataParams = Parameters<typeof buildGridGeodataPayload>[1];

const normalizeStoredPreviewFile = (value: unknown) => {
  if (!value) {
    return null;
  }

  if (typeof File !== 'undefined' && value instanceof File) {
    return {
      name: value.name,
      size: value.size,
      type: value.type,
      lastModified: value.lastModified,
    };
  }

  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    const blobWithName = value as Blob & { name?: unknown };
    return {
      name: typeof blobWithName.name === 'string' ? blobWithName.name : null,
      size: value.size,
      type: value.type,
    };
  }

  if (typeof value === 'object') {
    const candidate = value as Record<string, unknown>;
    return {
      id: candidate.id ?? null,
      name: candidate.name ?? candidate.fileName ?? null,
      path: candidate.path ?? candidate.url ?? null,
    };
  }

  return String(value);
};

const parsePreviewBooleanFlag = (value: unknown, fallback = false): boolean => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    if (value === '1' || value.toLowerCase() === 'true') return true;
    if (value === '0' || value.toLowerCase() === 'false') return false;
  }
  return Boolean(value);
};

const buildComparablePreviewSnapshot = ({
  gridParams,
  geodata,
  geodataParams,
}: {
  gridParams: RawGridParams;
  geodata: ComparablePreviewGeodata;
  geodataParams: ComparablePreviewGeodataParams;
}) => {
  const gridPayload = buildGridParamsPayload(gridParams);
  const geodataPayload = buildGridGeodataPayload(geodata, geodataParams);

  const trackedFiles = PREVIEW_TRACKED_GRID_FILE_KEYS.reduce<Record<string, unknown>>((files, key) => {
    files[key] = normalizeStoredPreviewFile(gridParams?.[key]);
    return files;
  }, {});
  for (const key of PREVIEW_TRACKED_GEODATA_FILE_KEYS) {
    trackedFiles[key] = normalizeStoredPreviewFile(geodataParams?.[key]);
  }

  return JSON.stringify({
    grid: gridPayload,
    geodata: {
      terrain: geodataPayload.terrain ?? null,
      building: geodataPayload.building ?? null,
      vegetation: geodataPayload.vegetation ?? null,
    },
    files: trackedFiles,
  });
};

const buildPersistedPreviewComparableState = (
  geodataData?: {
    name?: string | null;
    description?: string | null;
    params?: Record<string, unknown> | null;
    buildingsEnabled?: unknown;
    treesEnabled?: unknown;
    terrain?: Record<string, unknown> | null;
    building?: Record<string, unknown> | null;
    vegetation?: Record<string, unknown> | null;
  } | null
) => {
  const persistedGeodataParams = {
    ...(geodataData?.params ?? {}),
    ...(geodataData?.terrain ?? {}),
    ...(geodataData?.building ?? {}),
    ...(geodataData?.vegetation ?? {}),
  };

  return {
    geodata: {
      name: geodataData?.name ?? '',
      description: geodataData?.description ?? '',
      createdAt: null,
      createdBy: null,
      terrainEnabled: true,
      buildingsEnabled: parsePreviewBooleanFlag(geodataData?.buildingsEnabled ?? geodataData?.building, false),
      treesEnabled: parsePreviewBooleanFlag(geodataData?.treesEnabled ?? geodataData?.vegetation, false),
      params: persistedGeodataParams as ComparablePreviewGeodata['params'],
    } as ComparablePreviewGeodata,
    geodataParams: persistedGeodataParams as ComparablePreviewGeodataParams,
  };
};
const buildPreviewSignature = ({
  gridParams,
  geodata,
  geodataParams,
}: {
  gridParams: RawGridParams;
  geodata: ComparablePreviewGeodata;
  geodataParams: ComparablePreviewGeodataParams;
}) =>
  buildComparablePreviewSnapshot({
    gridParams,
    geodata,
    geodataParams,
  });

const normalizeStep = (value: string | null): FlowStep => {
  if (value === 'grid' || value === 'geodata' || value === 'simulation') {
    return value;
  }

  return 'project';
};

const buildFlowTitle = (step: FlowStep) => (
  <span className="font-normal text-slate-500">
    <span className={step === 'project' ? 'font-semibold text-slate-900' : undefined}>Add Project</span>
    {' > '}
    <span className={step === 'grid' || step === 'geodata' ? 'font-semibold text-slate-900' : undefined}>
      Add Grid & Geodata
    </span>
    {' > '}
    <span className={step === 'simulation' ? 'font-semibold text-slate-900' : undefined}>Add Simulations</span>
  </span>
);

const getDocumentTitleForStep = (step: FlowStep) => {
  if (step === 'simulation') return 'Add Simulations';
  if (step === 'grid' || step === 'geodata') return 'Add Grid';
  return 'Add Project';
};

const ProjectCreationFlowContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewContext = useContext(ViewContext);
  const { currentProject } = useContext(CurrentProjectContext);
  const { grid, gridParams, cloneGridData } = useContext(GridContext);
  const { geodata, geodataParams, cloneGeodataData } = useContext(GeodataContext);
  const flowGeodata = geodata as { progressStatus?: string | null; status?: string | null } | null;

  const step = normalizeStep(searchParams.get('step'));
  const projectId = searchParams.get('projectId');
  const gridId = searchParams.get('gridId');
  const geodataId = searchParams.get('geodataId');
  const simulationSetId = searchParams.get('simulationSetId');
  const [flowGeodataStatus, setFlowGeodataStatus] = useState<string | null>(null);
  const [isGeodataPollingEnabled, setIsGeodataPollingEnabled] = useState(false);
  const [persistentPreview, setPersistentPreview] = useState<ProjectCreationPreviewState | null>(null);
  const [previewRect, setPreviewRect] = useState<Pick<DOMRect, 'top' | 'left' | 'width' | 'height'> | null>(null);
  const [lastStablePreviewRect, setLastStablePreviewRect] = useState<Pick<
    DOMRect,
    'top' | 'left' | 'width' | 'height'
  > | null>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [lastSavedPreviewSignature, setLastSavedPreviewSignature] = useState<string | null>(null);
  const [isHydratingFlowState, setIsHydratingFlowState] = useState(false);
  const [volumeUsage, setVolumeUsage] = useState<GridVolumeUsage | null>(null);
  const [isVolumeUsageLoading, setIsVolumeUsageLoading] = useState(false);
  const [isVolumeDialogOpen, setIsVolumeDialogOpen] = useState(false);
  const pendingRunRef = useRef<(() => Promise<void>) | null>(null);
  const previewHostRef = useRef<HTMLDivElement | null>(null);
  const notifiedFailureStatusRef = useRef<string | null>(null);
  const lastHydratedResourceKeyRef = useRef<string | null>(null);
  const pendingHydratedBaselineKeyRef = useRef<string | null>(null);

  const handlePreviewStateChange = useCallback((nextPreview: ProjectCreationPreviewState | null) => {
    if (nextPreview) {
      setPersistentPreview(nextPreview);
      setIsPreviewVisible(true);
      return;
    }

    setIsPreviewVisible(false);
  }, []);
  const handlePreviewRectChange = useCallback((nextRect: Pick<DOMRect, 'top' | 'left' | 'width' | 'height'> | null) => {
    setPreviewRect(nextRect);
    if (nextRect) {
      setLastStablePreviewRect(nextRect);
    }
  }, []);
  const handleGeodataStatus = useCallback(
    (nextStatus: string | null) => {
      setFlowGeodataStatus(nextStatus);
      if (!nextStatus || !GEODATA_FAILURE_STATUSES.has(nextStatus)) {
        notifiedFailureStatusRef.current = null;
      }

      if (nextStatus && GEODATA_BUSY_STATUSES.has(nextStatus)) {
        setIsGeodataPollingEnabled(true);
        return;
      }

      setIsGeodataPollingEnabled(false);
      if (nextStatus && GEODATA_FAILURE_STATUSES.has(nextStatus) && notifiedFailureStatusRef.current !== nextStatus) {
        notifiedFailureStatusRef.current = nextStatus;
        viewContext.notification.show(GEODATA_FAILURE_MESSAGE, 'error', true, 'toast');
      }
    },
    [viewContext.notification]
  );
  const persistGridDraft = useCallback(
    async (targetGridId?: string | null, gridResolution?: number) => {
      if (!targetGridId) {
        return;
      }

      const remainingGeodatas = currentProject?.geodatasAvailableCount ?? null;
      const geodataLimitMessage = getGeodataModuleLimitMessage(
        remainingGeodatas,
        getGeodataRequiredModuleCount(geodata)
      );
      if (geodataLimitMessage) throw new Error(geodataLimitMessage);

      const nextGridParams = gridResolution === undefined ? gridParams : { ...gridParams, gridResolution };
      const requestPreviewSignature = buildPreviewSignature({
        gridParams: nextGridParams as RawGridParams,
        geodata,
        geodataParams,
      });

      const params = (await enrichGridParamsPayloadWithHeight(
        buildGridParamsPayload(nextGridParams as RawGridParams)
      )) as NonNullable<GridInsert['params']>;

      const persistedGrid = await replaceGrid(String(targetGridId), {
        name: grid.name,
        description: grid.description || null,
        params,
        geodata: buildGridGeodataReplacePayload(geodata, geodataParams),
      });

      setLastSavedPreviewSignature(requestPreviewSignature);
      const nextStatus =
        typeof (persistedGrid as { progressStatus?: unknown })?.progressStatus === 'string'
          ? ((persistedGrid as { progressStatus?: string }).progressStatus ?? null)
          : typeof flowGeodata?.progressStatus === 'string'
            ? flowGeodata.progressStatus
            : typeof flowGeodata?.status === 'string'
              ? flowGeodata.status
              : null;

      handleGeodataStatus(nextStatus);

      return persistedGrid;
    },
    [
      flowGeodata?.progressStatus,
      flowGeodata?.status,
      currentProject?.geodatasAvailableCount,
      geodata,
      geodataId,
      geodataParams,
      grid.description,
      grid.name,
      gridParams,
      handleGeodataStatus,
    ]
  );

  const persistGridDraftForPreview = useCallback(
    async (targetGridId?: string | null) => {
      await persistGridDraft(targetGridId);
    },
    [persistGridDraft]
  );

  const persistGridDraftSilently = useCallback(
    async (targetGridId?: string | null, gridResolution?: number) => {
      return persistGridDraft(targetGridId, gridResolution);
    },
    [persistGridDraft]
  );

  const persistGridDraftSilentlyForPreview = useCallback(
    async (targetGridId: string) => {
      await persistGridDraftSilently(targetGridId);
    },
    [persistGridDraftSilently]
  );

  const setFlowState = useCallback(
    (
      nextStep: FlowStep,
      ids: {
        projectId?: string | null;
        gridId?: string | null;
        geodataId?: string | null;
        simulationSetId?: string | null;
      } = {}
    ) => {
      const nextParams = new URLSearchParams();
      nextParams.set('step', nextStep);

      const nextProjectId = ids.projectId ?? projectId ?? null;
      const nextGridId = ids.gridId ?? gridId ?? null;
      const nextGeodataId = ids.geodataId ?? geodataId ?? null;
      const nextSimulationSetId = ids.simulationSetId ?? simulationSetId ?? null;

      if (nextProjectId) {
        nextParams.set('projectId', nextProjectId);
      }

      if (nextGridId) {
        nextParams.set('gridId', nextGridId);
      }

      if (nextGeodataId) {
        nextParams.set('geodataId', nextGeodataId);
      }
      if (nextSimulationSetId) {
        nextParams.set('simulationSetId', nextSimulationSetId);
      }

      navigate(
        {
          pathname: '/projects/add',
          search: `?${nextParams.toString()}`,
        },
        { replace: true }
      );
    },
    [geodataId, gridId, navigate, projectId, simulationSetId]
  );

  const safeStep = useMemo<FlowStep>(() => {
    if (step === 'simulation' && (!projectId || !gridId || !geodataId)) {
      if (projectId && gridId) {
        return 'grid';
      }
      return projectId ? 'grid' : 'project';
    }

    if (step === 'geodata' && (!projectId || !gridId)) {
      return projectId ? 'grid' : 'project';
    }

    if (step === 'grid' && !projectId) {
      return 'project';
    }

    return step;
  }, [geodataId, gridId, projectId, step]);
  const previewTrackedStepKey = useMemo(() => {
    if (safeStep === 'grid' && gridId) {
      return `grid:${gridId}:${geodataId ?? ''}`;
    }

    if (safeStep === 'geodata' && gridId) {
      return `geodata:${gridId}:${geodataId ?? ''}`;
    }

    return null;
  }, [geodataId, gridId, safeStep]);
  const currentPreviewSignature = useMemo(() => {
    if (!previewTrackedStepKey) {
      return null;
    }

    return buildPreviewSignature({
      gridParams: gridParams as RawGridParams,
      geodata,
      geodataParams,
    });
  }, [geodata, geodataParams, gridParams, previewTrackedStepKey]);
  const hydratedResourceKey = `${gridId ?? ''}:${geodataId ?? ''}`;

  useEffect(() => {
    if (
      isHydratingFlowState ||
      !currentPreviewSignature ||
      pendingHydratedBaselineKeyRef.current !== hydratedResourceKey
    ) {
      return;
    }

    setLastSavedPreviewSignature(currentPreviewSignature);
    pendingHydratedBaselineKeyRef.current = null;
  }, [currentPreviewSignature, hydratedResourceKey, isHydratingFlowState]);

  const isAwaitingHydratedBaseline =
    Boolean(gridId) &&
    (lastHydratedResourceKeyRef.current !== hydratedResourceKey ||
      pendingHydratedBaselineKeyRef.current === hydratedResourceKey);
  const hasUnsavedPreviewChanges =
    safeStep !== 'simulation' &&
    !isAwaitingHydratedBaseline &&
    Boolean(previewTrackedStepKey && currentPreviewSignature && lastSavedPreviewSignature !== currentPreviewSignature);

  const loadVolumeUsage = useCallback(async () => {
    if (!gridId) return null;
    setIsVolumeUsageLoading(true);
    try {
      const usage = await getGridVolumeUsage(String(gridId));
      setVolumeUsage(usage);
      return usage;
    } catch (error) {
      viewContext.notification.show(
        getApiErrorMessage(error, 'Unable to load volume usage right now.'),
        'error',
        true,
        'toast'
      );
      return null;
    } finally {
      setIsVolumeUsageLoading(false);
    }
  }, [gridId, viewContext.notification]);

  useEffect(() => {
    if (safeStep !== 'simulation' || !gridId) {
      setVolumeUsage(null);
      return;
    }
    void loadVolumeUsage();
  }, [gridId, loadVolumeUsage, safeStep]);

  const confirmVolumeUsageAndRun = async (run: () => Promise<void>) => {
    const usage = await loadVolumeUsage();
    if (!usage) return;
    if (usage.volumeDifference <= 0) {
      await run();
      return;
    }
    pendingRunRef.current = run;
    setIsVolumeDialogOpen(true);
  };

  const handleExit = useCallback(() => {
    if (projectId) {
      navigate(`/projects/${projectId}`);
      return;
    }

    navigate('/dashboard');
  }, [navigate, projectId]);

  useEffect(() => {
    const resourceKey = `${gridId ?? ''}:${geodataId ?? ''}`;
    const shouldHydrate = Boolean(gridId || geodataId);

    if (!shouldHydrate) {
      lastHydratedResourceKeyRef.current = null;
      pendingHydratedBaselineKeyRef.current = null;
      setIsHydratingFlowState(false);
      return;
    }

    if (lastHydratedResourceKeyRef.current === resourceKey) {
      return;
    }

    let cancelled = false;

    const hydrateFlowState = async () => {
      setIsHydratingFlowState(true);

      try {
        const [gridData, geodataData] = await Promise.all([
          gridId ? getGrid(String(gridId)) : Promise.resolve(null),
          geodataId ? getGeodata(String(geodataId)) : Promise.resolve(null),
        ]);

        if (cancelled) {
          return;
        }

        if (gridData) {
          cloneGridData(gridData, { appendClonedSuffix: false });
        }

        if (geodataData) {
          cloneGeodataData(geodataData as Parameters<typeof cloneGeodataData>[0], { appendClonedSuffix: false });
          const hydratedStatus =
            typeof geodataData.progressStatus === 'string'
              ? geodataData.progressStatus
              : typeof geodataData.status === 'string'
                ? geodataData.status
                : null;
          handleGeodataStatus(hydratedStatus);
        }

        if (gridData) {
          const persistedPreviewComparableState = buildPersistedPreviewComparableState(
            (geodataData ?? null) as Parameters<typeof buildPersistedPreviewComparableState>[0]
          );
          setLastSavedPreviewSignature(
            buildComparablePreviewSnapshot({
              gridParams: (gridData?.params ?? {}) as RawGridParams,
              geodata: persistedPreviewComparableState.geodata,
              geodataParams: persistedPreviewComparableState.geodataParams,
            })
          );
          pendingHydratedBaselineKeyRef.current = resourceKey;
        }

        lastHydratedResourceKeyRef.current = resourceKey;
      } catch (error) {
        if (!cancelled) {
          console.error('Error hydrating project creation flow state:', error);
        }
      } finally {
        if (!cancelled) {
          setIsHydratingFlowState(false);
        }
      }
    };

    void hydrateFlowState();

    return () => {
      cancelled = true;
    };
  }, [cloneGeodataData, cloneGridData, geodataId, gridId, handleGeodataStatus]);

  useEffect(() => {
    if (!geodataId) {
      setFlowGeodataStatus(null);
      setIsGeodataPollingEnabled(false);
      notifiedFailureStatusRef.current = null;
      return;
    }
  }, [geodataId]);

  useEffect(() => {
    if (!geodataId || !isGeodataPollingEnabled) {
      return;
    }

    let cancelled = false;
    let timeoutId: number | null = null;
    const startedAt = Date.now();

    const scheduleNextPoll = () => {
      if (cancelled) {
        return;
      }
      timeoutId = window.setTimeout(
        () => {
          void pollStatus();
        },
        getGeodataPollingDelayMs(Date.now() - startedAt)
      );
    };

    const pollStatus = async () => {
      let nextStatus: string | null = null;

      try {
        const progress = await getProgress({ geodataIds: [String(geodataId)] });
        if (cancelled) return;

        const payload = progress?.geodatas?.[String(geodataId)] ?? null;
        nextStatus =
          typeof payload?.state === 'string'
            ? payload.state
            : typeof flowGeodata?.progressStatus === 'string'
              ? flowGeodata.progressStatus
              : typeof flowGeodata?.status === 'string'
                ? flowGeodata.status
                : null;

        setFlowGeodataStatus(nextStatus);
        if (!nextStatus || !GEODATA_FAILURE_STATUSES.has(nextStatus)) {
          notifiedFailureStatusRef.current = null;
        }
        if (nextStatus && GEODATA_POLLING_STOP_STATUSES.has(nextStatus)) {
          window.dispatchEvent(new Event(VISUALIZER_PREVIEW_RELOAD_EVENT));
          setIsGeodataPollingEnabled(false);
          return;
        }
        if (nextStatus && GEODATA_FAILURE_STATUSES.has(nextStatus)) {
          setIsGeodataPollingEnabled(false);
          if (notifiedFailureStatusRef.current !== nextStatus) {
            notifiedFailureStatusRef.current = nextStatus;
            viewContext.notification.show(GEODATA_FAILURE_MESSAGE, 'error', true, 'toast');
          }
          return;
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error polling project creation geodata progress:', error);
        }
      }

      scheduleNextPoll();
    };

    void pollStatus();

    return () => {
      cancelled = true;
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [flowGeodata?.progressStatus, flowGeodata?.status, geodataId, isGeodataPollingEnabled, viewContext.notification]);

  const isFlowGeodataBusy = flowGeodataStatus != null && GEODATA_BUSY_STATUSES.has(flowGeodataStatus);
  const isFlowGeodataFailed = flowGeodataStatus != null && GEODATA_FAILURE_STATUSES.has(flowGeodataStatus);
  const effectivePreviewRect = previewRect ?? lastStablePreviewRect;
  const flowTitle = buildFlowTitle(safeStep);

  useLayoutEffect(() => {
    document.title = getDocumentTitleForStep(safeStep);
  });

  const previewElement =
    persistentPreview && effectivePreviewRect ? (
      <ProjectCreationPersistentPreview
        preview={{ ...persistentPreview, hasUnsavedChanges: hasUnsavedPreviewChanges }}
        visible={isPreviewVisible}
        style={{
          top: effectivePreviewRect.top,
          left: effectivePreviewRect.left,
          width: effectivePreviewRect.width,
          height: effectivePreviewRect.height,
        }}
      />
    ) : null;

  if (isHydratingFlowState) {
    return (
      <div className="review-loader-center">
        <UMLoader />
      </div>
    );
  }

  if (safeStep === 'project') {
    return (
      <AddProject
        breadcrumbTitle={flowTitle}
        onProjectCreated={async (createdProjectId) => {
          setFlowState('grid', { projectId: createdProjectId, gridId: null });
        }}
      />
    );
  }

  let flowStepContent: ReactNode = null;

  if (safeStep === 'grid' && projectId) {
    flowStepContent = (
      <AddGrid
        breadcrumbTitle={flowTitle}
        projectIdOverride={projectId}
        gridIdOverride={gridId}
        draftGeodataId={geodataId}
        disableAdvanceActions={isFlowGeodataBusy}
        onUpdatePreview={persistGridDraftForPreview}
        onExitOverride={handleExit}
        onPreviewStateChange={handlePreviewStateChange}
        onPreviewSlotRectChange={handlePreviewRectChange}
        previewRectAnchor={previewHostRef.current}
        onDraftResourcesChange={async ({ gridId: nextGridId, geodataId: nextGeodataId, progressStatus }) => {
          handleGeodataStatus(progressStatus ?? 'INITIALIZING');
          setFlowState('grid', { projectId, gridId: nextGridId, geodataId: nextGeodataId });
        }}
        onPersistDraftGrid={persistGridDraftSilently}
        onPersistDraftGridForSimulation={async (targetGridId) => {
          await persistGridDraft(targetGridId);
        }}
        onOpenGeodataSettings={async (createdGridId) => {
          setFlowState('geodata', { projectId, gridId: createdGridId, geodataId });
        }}
        onOpenSimulationSettings={async (createdGridId) => {
          setFlowState('simulation', { projectId, gridId: createdGridId, geodataId });
        }}
      />
    );
  }

  if (safeStep === 'geodata' && gridId) {
    flowStepContent = (
      <AddGeodata
        breadcrumbTitle={flowTitle}
        gridIdOverride={gridId}
        previewLoading={isFlowGeodataBusy}
        onUpdatePreview={persistGridDraftForPreview}
        onExitOverride={handleExit}
        onPreviewStateChange={handlePreviewStateChange}
        onPreviewSlotRectChange={handlePreviewRectChange}
        previewRectAnchor={previewHostRef.current}
        onCloseOverride={(savedGeodataId) => {
          setFlowState('grid', { projectId, gridId, geodataId: savedGeodataId ?? geodataId });
        }}
      />
    );
  }

  if (safeStep === 'simulation' && geodataId) {
    flowStepContent = (
      <AddSimulation
        breadcrumbTitle={flowTitle}
        geodataIdOverride={geodataId}
        gridIdOverride={gridId}
        projectIdOverride={projectId}
        simulationSetIdOverride={simulationSetId}
        onExitOverride={handleExit}
        onBackOverride={() => {
          setFlowState('grid', { projectId, gridId, geodataId });
        }}
        disableRun={isFlowGeodataBusy || isFlowGeodataFailed}
        isVolumeUsageLoading={isVolumeUsageLoading}
        onConfirmVolumeUsage={confirmVolumeUsageAndRun}
        previewLoading={isFlowGeodataBusy}
        onUpdatePreview={persistGridDraftSilentlyForPreview}
        onPreviewStateChange={handlePreviewStateChange}
        onPreviewSlotRectChange={handlePreviewRectChange}
        previewRectAnchor={previewHostRef.current}
      />
    );
  }

  return (
    <div ref={previewHostRef} className="project-creation-preview-host">
      {flowStepContent}
      {previewElement}
      <VolumeUsageDialog
        open={isVolumeDialogOpen}
        gridId={gridId ? String(gridId) : null}
        initialUsage={volumeUsage}
        onClose={() => {
          pendingRunRef.current = null;
          setIsVolumeDialogOpen(false);
        }}
        onConsumed={async (usage) => {
          setVolumeUsage(usage);
          await pendingRunRef.current?.();
        }}
      />
    </div>
  );
};

const ProjectCreationFlow = () => {
  const [currentProject, setCurrentProject] = useState<ProjectNode | null>(null);
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');

  useEffect(() => {
    if (!projectId) {
      setCurrentProject(null);
      return;
    }

    let cancelled = false;

    const loadProject = async () => {
      try {
        const project = await getProjectWithChildren(String(projectId));
        if (cancelled || !project) {
          return;
        }

        setCurrentProject(
          normalizeProjectCoordinatesForMap({
            ...project,
            coordinates: ((project as { projectCoordinates?: unknown }).projectCoordinates ??
              undefined) as ProjectNode['coordinates'],
          }) as unknown as ProjectNode
        );
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading project creation flow project:', error);
        }
      }
    };

    void loadProject();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return (
    <CurrentProjectContext.Provider value={{ currentProject, setCurrentProject }}>
      <GridContextProvider>
        <GeodataContextProvider>
          <SimulationContextProvider>
            <ProjectCreationFlowContent />
          </SimulationContextProvider>
        </GeodataContextProvider>
      </GridContextProvider>
    </CurrentProjectContext.Provider>
  );
};

export default ProjectCreationFlow;
